import Icon from "@mdi/react";
import Typography from "@material-ui/core/Typography";
import socketIOClient from "socket.io-client";
import styled from "@emotion/styled";
import React, { FC, useCallback, useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { mdiCloudUpload } from "@mdi/js";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";

import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import CompaniesDashboard from "./components/CompaniesDashboard";
import DropZone from "../../../components/elements/DropZone";
import ProfileTierSelector from "./components/ProfileTierSelector";
import classes from "./SelectHubspotTargetCompaniesSlide.module.scss";
import { PageStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { SelectOptionType } from "../../../components/elements/Select/Select";
import { errorToaster } from "../../../helpers/toasters";
import {
  getCampaignCompanySortValues,
  getCampaignFoundCompaniesInfo,
  setCampaignToCreateSelectedAdvertiser,
  setFoundCompanyInfo,
  setFromFile,
} from "../../../redux/reducers/campaignToCreate";

const StyledDropzone = styled.section`
  flex: 1;
  height: 16rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 4px 4px 4px;
  border-width: 3px;
  border-radius: 4px;
  border-color: #0083ef;
  border-style: dashed;
  background-color: #fafafa;
  color: black;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
`;

const SelectHubspotTargetCompaniesSlide: FC<PageStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}) => {
  const [tierValue, setTierValue] = useState<string[]>([]);
  const [areTargetAccountsOnly, setAreTargetAccountsOnly] = useState<boolean>(false);
  const [areCompaniesLoading, setAreCompaniesLoading] = useState<boolean>(false);
  const [queryOpts, setQueryOpts] = useState<Array<any>>([]);
  const [didErrorOccurInUpload, setUploadError] = useState<boolean>(false);

  const dispatch = useDispatch();

  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);

  const apiError = useSelector((state: ReduxApplicationState) => state.apiError);

  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState("");
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);

  const validateCSV = useCallback((csvRowsWithoutHeader: string[]) => {
    const domainRegex = /^(?!:\/\/)([\dA-Za-z-]+\.)+[A-Za-z]{2,}$/;

    const validatedDomains: string[] = [];
    const invalidDomains: string[] = [];
    let errorMessage = "";

    for (const csvRow of csvRowsWithoutHeader) {
      if (csvRow.length === 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const parts = csvRow.split(",");

      for (const part of parts) {
        const domain = part.trim();

        if (!domainRegex.test(domain)) {
          invalidDomains.push(domain);
          // eslint-disable-next-line no-continue
          continue;
        }

        validatedDomains.push(domain);
      }
    }

    if (invalidDomains.length > 0) {
      errorMessage += ` Wrong domains: ${invalidDomains.join(", ")}`;
    }

    return { validatedDomains, errorMessage };
  }, []);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setUploadError(false);
      acceptedFiles.forEach(async (file: any) => {
        try {
          const reader = new FileReader();
          reader.readAsText(file);
          reader.addEventListener("load", async (event) => {
            const csv: string = event.target!.result as string;
            // split the csv by new line
            const csvRows = csv.split("\n");

            const { validatedDomains, errorMessage } = validateCSV(csvRows);
            if (Array.isArray(validatedDomains)) {
              setAreCompaniesLoading(true);

              const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

              socket.emit("getCSVKickfireMatches", validatedDomains);

              socket.on("retrievedCSVKickfireMatches", (response: any) => {
                console.log("retrievedCSVKickfireMatchesresponse:", response);
                if (!response.success) {
                  errorToaster(response.error);
                } else {
                  dispatch(setFoundCompanyInfo(response.payload));
                  dispatch(setFromFile(file.name));
                }
                setAreCompaniesLoading(false);
              });
            }
            if (errorMessage) {
              errorToaster(errorMessage, 10000);
            }
          });
        } catch (error) {
          console.log("There was an error!:", error);
          setUploadError(true);
        }
      });
    },
    [dispatch, validateCSV],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  const isCompanySortValuesPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);

  const companySortValues = useSelector((state: ReduxApplicationState) => state.campaignToCreate.companySortValues);
  const foundCompaiesInfo = useSelector((state: ReduxApplicationState) => state.campaignToCreate.foundCompanyInfo);

  const companySortValuesOptions: SelectOptionType[] = companySortValues?.tier_values.map((tier_value) => ({
    label: tier_value.label,
    value: tier_value.value,
  }))!;

  const handleAreTargetsAccountOnlyChange = (_: React.ChangeEvent<{}>, checked: boolean) => {
    setAreTargetAccountsOnly(checked);
  };

  const handleGetCompanyNames = async () => {
    try {
      if (tierValue.length > 0) {
        setAreCompaniesLoading(true);
        if (areTargetAccountsOnly) {
          dispatch(
            getCampaignFoundCompaniesInfo(
              [...queryOpts, { operator: "EQ", propertyName: "hs_is_target_account", value: "true" }],
              setAreCompaniesLoading,
            ),
          );
        } else {
          dispatch(getCampaignFoundCompaniesInfo(queryOpts, setAreCompaniesLoading));
        }
        dispatch(setFromFile(null));
      } else {
        errorToaster("Select a HubSpot Ideal Customer Profile Tier to get Companies.", 5000);
      }
    } catch (error) {
      console.log("Error in handleGetCompanyNames: ", error);
      errorToaster(error, 5000);
    }
  };

  useEffect(() => {
    if (apiError.apiError) errorToaster(apiError.apiError, 5000);
  }, [apiError]);

  const updateQueryOptions = (propertyName: string, operator: string, value: string[]) => {
    type QueryObject = {
      propertyName: string;
      operator: string;
      values: string[];
    };

    const queryObject: QueryObject = {
      operator,
      propertyName,
      values: value,
    };
    // ?
    queryOpts.push(queryObject);
    setQueryOpts(queryOpts);
  };

  const handleProfileTierChange = (e: any) => {
    queryOpts.splice(0, queryOpts.length);
    setTierValue(e.target.value);
    updateQueryOptions("hs_ideal_customer_profile", "IN", e.target.value);
  };

  useEffect(() => {
    if (!companySortValues && selectedAdvertiser?.hubspot_authed) {
      dispatch(getCampaignCompanySortValues());
    }
  }, [companySortValues, dispatch, selectedAdvertiser]);

  useEffect(() => {
    if (isAttemptingToGoNext && handleNext && setIsAttemptingToGoNext) {
      if (
        foundCompaiesInfo?.primarySegments?.length ||
        foundCompaiesInfo?.uniqueSegments?.length ||
        // eslint-disable-next-line no-restricted-globals, no-alert
        confirm(
          "You are about to create a blank ABM campaign, It is assumed that you will use the BrandGen Hubspot integrations to add companies from Hubspot at a later point in time. Are you sure you want to continue?",
        )
      ) {
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster('Please, choose a HubSpot Ideal Customer Profile Tier and select "Get Company Names".', 15000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [foundCompaiesInfo, handleNext, isAttemptingToGoNext, setIsAttemptingToGoNext, tierValue]);

  useEffect(() => {
    if (!selectedAdvertiserId && setIsAttemptingToGoNext) return;

    const findedAdvertiser = advertisers.find((advertiser) => advertiser.brandgenAdvertiserId === selectedAdvertiserId);

    if (findedAdvertiser) dispatch(setCampaignToCreateSelectedAdvertiser(findedAdvertiser));
  }, [advertisers, dispatch, selectedAdvertiserId, setIsAttemptingToGoNext]);

  const mainContent = () => (
    <>
      {isCompanySortValuesPending ? (
        <Typography className={classes.helperLabel}>Please, wait while we get everything in order...</Typography>
      ) : !isCompanySortValuesPending && companySortValuesOptions?.length ? (
        <ProfileTierSelector
          tierValue={tierValue}
          options={companySortValuesOptions}
          areTargetAccountsOnly={areTargetAccountsOnly}
          handleAreTargetsAccountOnlyChange={handleAreTargetsAccountOnlyChange}
          handleGetCompanyNames={handleGetCompanyNames}
          handleProfileTierChange={handleProfileTierChange}
        />
      ) : (
        <Typography className={classes.helperLabel}>
          {selectedAdvertiser?.hubspot_authed
            ? "No Profile Tiers found linked to this advertiser's HubSpot Account."
            : ""}
        </Typography>
      )}

      {!areCompaniesLoading && !isCompanySortValuesPending && (
        <>
          {selectedAdvertiser?.hubspot_authed && (
            <div
              style={{
                width: "100%",
                height: "20px",
                borderBottom: "1px solid #C0C0C0",
                textAlign: "center",
                margin: "4rem 0",
              }}
            >
              <span
                style={{
                  fontSize: "25px",
                  backgroundColor: setIsAttemptingToGoNext ? "white" : "#fafafa",
                  padding: "0 10px",
                }}
              >
                OR
              </span>
            </div>
          )}

          <Typography variant="h2" className={!setIsAttemptingToGoNext ? classes.h2Title : ""}>
            Upload Target Company via CSV
          </Typography>
          <DropZone
            explanatoryText="Please upload an CSV file that has one column - Company Domain. Each domain should be on a new line."
            sampleLink="https://www.brandgen.io/hubfs/Help%20Documents/BG_Sample.csv"
            onDrop={onDrop}
            didErrorOccurInUpload={didErrorOccurInUpload}
          />
        </>
      )}

      {!isCompanySortValuesPending ? (
        <CompaniesDashboard
          tierValue={tierValue}
          areTargetAccountsOnly={areTargetAccountsOnly}
          areCompaniesLoading={areCompaniesLoading}
          primarySegments={foundCompaiesInfo?.primarySegments}
          uniqueSegments={foundCompaiesInfo?.uniqueSegments}
        />
      ) : null}
    </>
  );

  return (
    <div
      style={{
        padding: !setIsAttemptingToGoNext ? "4rem" : 0,
      }}
    >
      {!setIsAttemptingToGoNext && (
        <div
          style={{
            margin: "3rem 0",
            width: "20rem",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Select Advertiser</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedAdvertiserId}
              label="Advertiser"
              onChange={(e) => {
                setSelectedAdvertiserId(e.target.value as string);
              }}
            >
              {advertisers.map((advertiser) => {
                return (
                  <MenuItem key={advertiser.brandgenAdvertiserId} value={advertiser.brandgenAdvertiserId}>
                    {advertiser.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}
      {setIsAttemptingToGoNext ? (
        <CampaignCreationSlideContainer
          heading={selectedAdvertiser?.hubspot_authed ? "Select HubSpot Target Companies" : ""}
        >
          {mainContent()}
        </CampaignCreationSlideContainer>
      ) : (
        mainContent()
      )}
    </div>
  );
};

export default SelectHubspotTargetCompaniesSlide;
